'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';
import type { ImageField, RichTextField } from '@prismicio/client';
import { isFilled } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { HorizontalContentCard } from '@/components/redesign/HorizontalContentCards';
import {
  StepBody,
  StepTitle,
} from '@/components/redesign/NumberedIconStepCard';

type Props = {
  step: {
    card_step: string | null;
    card_icon: ImageField;
    card_title: RichTextField;
    card_description: RichTextField;
  };
};

// `useLayoutEffect` warns during SSR because it can't measure the DOM there.
// Fall back to `useEffect` on the server (where it is a no-op anyway) so the
// warning doesn't fire; on the client we still use `useLayoutEffect` so the
// tile is capped before the first paint and there's no visible flash.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const MOBILE_ICON_SIZE_PX = 86;

/**
 * Card layout for the auto-scrolling variation of `StackedHeaderCardsRow`.
 *
 * Text column keeps the editorially-driven step label (a year, a phase, any
 * string) at the top so authors can label cards however they want. Icon
 * rendering mirrors `RightIconStepCard` on purpose — same right-side tile
 * that stretches to the card's height with a JS-driven 4:3 width cap on
 * sm+, and the same 86px badge fallback on mobile — so both variants of
 * this slice family present the icon the same way.
 *
 * Card sizing lives here (not in `MarqueeCardsRow`) because it's the card's
 * concern: `w-[45vw]` with min/max clamps keeps it readable on phones and
 * from stretching on ultra-wide monitors. Height is content-driven.
 *
 * Adds the same subtle lift + shadow highlight on hover as the other cards
 * in this family, layered on top of the marquee's pause-on-hover.
 */
export function AutoScrollStepCard({ step }: Props) {
  const hasIcon = isFilled.image(step.card_icon);
  const textRef = useRef<HTMLDivElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    if (!hasIcon) {
      return;
    }
    const text = textRef.current;
    const tile = tileRef.current;
    if (!text || !tile) {
      return;
    }

    const compute = () => {
      // When the sm+ tile is hidden (mobile viewport, CSS `hidden`), skip:
      // there's nothing to cap and the tile isn't participating in layout.
      if (tile.offsetParent === null) {
        tile.style.maxWidth = '';
        return;
      }

      // Measure the text column's *minimum* height by temporarily removing
      // the sm+ tile from layout — the text then renders at maximum
      // available width and produces the shortest possible height. Any
      // width we later give the tile makes the text narrower, so its real
      // height is always ≥ this measurement.
      const previousDisplay = tile.style.display;
      tile.style.display = 'none';
      const minHeight = text.getBoundingClientRect().height;
      tile.style.display = previousDisplay;

      // Cap tile width at 4/3 of that minimum height. Because the tile
      // stretches to the actual (≥ minHeight) row height, the rendered
      // width/height ratio ends up ≤ 4/3 — never wider than 4:3.
      const cap = Math.round(minHeight * (4 / 3));
      tile.style.maxWidth = `${cap}px`;
    };

    compute();
    window.addEventListener('resize', compute);
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(compute).catch(() => {});
    }
    return () => window.removeEventListener('resize', compute);
  }, [hasIcon]);

  return (
    <HorizontalContentCard
      className={`flex h-full flex-col gap-4 p-6 transition-all duration-300 ease-out hover:-translate-y-[2px] hover:shadow-[0_12px_28px_rgba(0,0,0,0.16)] sm:flex-row sm:items-stretch sm:gap-6 ${
        hasIcon
          ? 'w-[54vw] min-w-[336px] max-w-[720px] md:w-[62vw] md:min-w-[386px] md:max-w-[828px] lg:w-[54vw] lg:min-w-[336px] lg:max-w-[720px]'
          : 'w-[45vw] min-w-[280px] max-w-[600px]'
      }`}
    >
      <div ref={textRef} className="flex min-w-0 flex-1 flex-col gap-4">
        {/*
          Header row: editorial step label always at the leading edge; the
          small mobile icon badge on the trailing edge is only shown below
          `sm`. `justify-between` positions the two ends at mobile, and
          gracefully collapses to a leading step label at sm+ when the icon
          is display: none.
        */}
        {(step.card_step || hasIcon) && (
          <div className="flex items-center justify-between">
            {step.card_step ? (
              <span className="border-l border-[#4b0fea] pl-3 text-[18px] font-medium leading-[1.5] text-[#444]">
                {step.card_step}
              </span>
            ) : (
              <span />
            )}
            {hasIcon && (
              <div
                className="relative shrink-0 rounded-[8px] sm:hidden"
                style={{
                  width: MOBILE_ICON_SIZE_PX,
                  height: MOBILE_ICON_SIZE_PX,
                }}
              >
                <PrismicNextImage
                  field={step.card_icon}
                  fill
                  sizes={`${MOBILE_ICON_SIZE_PX}px`}
                  className="object-contain"
                  fallbackAlt=""
                />
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <StepTitle
            field={step.card_title}
            className="text-[22px] font-semibold leading-[1.5] text-[#041118]"
          />
          <StepBody
            field={step.card_description}
            className="text-[16px] font-light leading-[1.5] text-[#041118] md:text-[18px]"
          />
        </div>
      </div>

      {hasIcon && (
        // sm+ tile — hidden below `sm` (mobile uses the badge inside the
        // header row instead). Fixed width per breakpoint, transparent
        // background, and the JS `max-width` cap enforces width ≤ 4/3 ×
        // height on the actual row height.
        <div
          ref={tileRef}
          className="relative hidden shrink-0 self-stretch rounded-[8px] sm:block sm:w-[160px] md:w-[180px] lg:w-[200px]"
        >
          <PrismicNextImage
            field={step.card_icon}
            fill
            sizes="200px"
            className="object-contain object-right"
            fallbackAlt=""
          />
        </div>
      )}
    </HorizontalContentCard>
  );
}
